<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 900px;">

      <!-- Sticky Header -->
      <div class="table-header-filters pa-4 mb-6">
        <div class="d-flex align-center">
          <v-icon size="20" color="#00838F" class="mr-2">mdi-email-fast-outline</v-icon>
          <span class="page-title">Automatsko slanje izvještaja</span>
          <v-spacer />
          <v-tooltip v-if="!showHelp" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" @click="showHelp = true">
                <v-icon size="18" color="#94A3B8">mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>Prikaži upute</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Info banner -->
      <div v-if="showHelp" class="info-banner mb-4">
        <v-btn icon x-small class="info-banner__close" @click="showHelp = false">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
        <div class="info-banner__schedule-times mb-3">
          <span class="info-banner__schedule-times-label">Kada se šalje:</span>
          <span class="info-banner__schedule-time-item"><strong>Dnevno</strong> — svaki dan ujutro za prethodni dan</span>
          <span class="info-banner__schedule-time-item"><strong>Mjesečno</strong> — 1. u mjesecu za prethodni mjesec</span>
          <span class="info-banner__schedule-time-item"><strong>Kvartalno</strong> — 1. u kvartalu za prethodni kvartal</span>
          <span class="info-banner__schedule-time-item"><strong>Godišnje</strong> — 1. siječnja za prethodnu godinu</span>
        </div>
        <p class="info-banner__intro">
          Odaberite raspored, uključite željeni tip izvještaja i unesite email adrese primatelja.
          Sustav će automatski generirati i poslati izvještaj prema gore navedenim vremenima.
        </p>
        <div class="info-banner__grid">
          <div class="info-legend-item">
            <v-icon size="16" color="#0891B2">mdi-toggle-switch-outline</v-icon>
            <span><strong>Uključi / Isključi</strong> — aktivira ili deaktivira automatsko slanje za taj tip izvještaja.</span>
          </div>
          <div class="info-legend-item">
            <v-icon size="16" color="#0891B2">mdi-file-document-outline</v-icon>
            <span><strong>Format</strong> — odaberite PDF, Excel ili oboje kao prilog emaila.</span>
          </div>
          <div class="info-legend-item">
            <v-icon size="16" color="#0891B2">mdi-email-outline</v-icon>
            <span><strong>Primatelji</strong> — unesite email adresu i pritisnite Enter za svaku osobu.</span>
          </div>
          <div class="info-legend-item">
            <v-icon size="16" color="#0891B2">mdi-history</v-icon>
            <span><strong>Povijest</strong> — pregled prethodnih slanja i njihovih statusa (sent / failed).</span>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <!-- Schedules -->
      <v-expansion-panels v-else v-model="openPanels" multiple flat>
        <v-expansion-panel
          v-for="schedule in config.schedules"
          :key="schedule"
          class="schedule-panel mb-3"
        >
          <v-expansion-panel-header class="panel-header">
            <div class="d-flex align-center">
              <v-icon size="18" color="#00838F" class="mr-3">{{ scheduleIcon(schedule) }}</v-icon>
              <span class="schedule-title">{{ scheduleLabel(schedule) }}</span>
              <v-chip
                x-small
                class="ml-3"
                :class="activeCount(schedule) > 0 ? 'active-chip' : 'inactive-chip'"
              >{{ activeCount(schedule) }} aktivno</v-chip>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="panel-content">
            <div class="reports-list">
              <div
                v-for="rt in reportTypesForSchedule(schedule)"
                :key="rt.id"
                class="sub-card"
                :class="{ 'sub-card--inactive': !row(schedule, rt.id).active }"
              >
                <!-- Sub-card header -->
                <div class="sub-card-header">
                  <span class="sub-card-title">{{ rt.name || rt.id }}</span>
                  <v-spacer />
                  <v-switch
                    :input-value="row(schedule, rt.id).active"
                    color="#00838F"
                    dense
                    inset
                    hide-details
                    class="row-switch"
                    @change="onToggle(schedule, rt.id, $event)"
                  />
                </div>

                <!-- Sub-card body + footer (only when active) -->
                <v-expand-transition>
                  <div v-if="row(schedule, rt.id).active">
                    <v-divider />

                    <!-- Two-column body -->
                    <div class="sub-card-body">
                      <!-- Left: Format -->
                      <div class="field-group">
                        <div class="field-label">Format</div>
                        <div class="format-pills">
                          <span
                            v-for="fmt in (rt.reportFormats || config.reportFormats)"
                            :key="fmt"
                            class="format-pill"
                            :class="{ active: row(schedule, rt.id).reportFormats.includes(fmt), [`active-${fmt}`]: row(schedule, rt.id).reportFormats.includes(fmt) }"
                            @click="toggleFormat(schedule, rt.id, fmt)"
                          >
                            <v-icon x-small class="mr-1">{{ formatIcon(fmt) }}</v-icon>
                            {{ formatLabel(fmt) }}
                          </span>
                        </div>
                      </div>

                      <!-- Right: Recipients -->
                      <div class="field-group">
                        <div class="field-label">Primatelji</div>
                        <v-combobox
                          :ref="`combobox-${schedule}-${rt.id}`"
                          :value="row(schedule, rt.id).recipients.filter(Boolean)"
                          @input="setRecipients(schedule, rt.id, $event)"
                          chips
                          multiple
                          deletable-chips
                          small-chips
                          append-icon=""
                          placeholder="email@primjer.com → Enter"
                          outlined
                          dense
                          hide-details
                          class="filter-select recipients-combobox"
                        >
                          <template #selection="{ item, select, selected, parent }">
                            <v-chip
                              :key="item"
                              small
                              close
                              class="recipient-chip"
                              @click:close="parent.selectItem(item)"
                            >{{ item }}</v-chip>
                          </template>
                        </v-combobox>
                      </div>
                    </div>

                    <!-- Location + options row (only for reports that require location) -->
                    <div v-if="rt.requiresLocation" class="sub-card-body sub-card-body--options">
                      <!-- Prodajno mjesto -->
                      <div class="field-group">
                        <div class="field-label">Prodajno mjesto</div>
                        <v-autocomplete
                          :value="row(schedule, rt.id).locationIds"
                          @change="v => $set(rows[schedule][rt.id], 'locationIds', v || [])"
                          :items="locations"
                          item-text="name"
                          item-value="id"
                          placeholder="Odaberite prodajna mjesta"
                          outlined
                          dense
                          hide-details
                          clearable
                          multiple
                          small-chips
                          deletable-chips
                          class="filter-select"
                        />
                      </div>
                      <!-- Neplaćeni računi -->
                      <div class="field-group field-group--switch">
                        <div class="field-label">Opcije</div>
                        <v-switch
                          :input-value="row(schedule, rt.id).showUnpaid"
                          @change="v => $set(rows[schedule][rt.id], 'showUnpaid', !!v)"
                          label="Uključi neplaćene račune"
                          hide-details
                          dense
                          class="mt-1"
                          color="#3989C9"
                        />
                      </div>
                    </div>

                    <!-- Sub-card footer -->
                    <div class="sub-card-footer">
                      <v-spacer />
                      <v-btn
                        v-if="row(schedule, rt.id).subId"
                        icon
                        small
                        class="log-btn mr-2"
                        @click="openLog(schedule, rt.id)"
                      >
                        <v-icon>mdi-history</v-icon>
                      </v-btn>
                      <v-btn
                        small
                        class="save-btn"
                        :loading="row(schedule, rt.id).saving"
                        @click="saveRow(schedule, rt.id)"
                      >
                        {{ row(schedule, rt.id).subId ? 'Spremi' : 'Kreiraj' }}
                      </v-btn>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Deactivate confirm -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">Isključi pretplatu</v-card-title>
          <v-card-text>
            Jeste li sigurni da želite ugasiti automatsko slanje
            <strong>{{ pendingDeactivate && reportTypeName(pendingDeactivate.typeId) }}</strong>
            za raspored <strong>{{ pendingDeactivate && scheduleLabel(pendingDeactivate.schedule) }}</strong>?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelDeactivate">Odustani</v-btn>
            <v-btn class="confirm-delete-btn" depressed small :loading="deactivating" @click="confirmDeactivate">
              Isključi
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>
  </v-container>
</template>

<script>
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import { df, auth } from '@/plugins/firebase'
import {
  getReportConfig,
  getSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription
} from '@/api/emailReportScheduler'

const SCHEDULE_LABELS = { daily: 'Dnevno', monthly: 'Mjesečno', quarterly: 'Kvartalno', yearly: 'Godišnje' }
const SCHEDULE_ICONS = { daily: 'mdi-calendar-today', monthly: 'mdi-calendar-month', quarterly: 'mdi-calendar-refresh', yearly: 'mdi-calendar-star' }

function emptyRow (rt) {
  return {
    active: false,
    subId: null,
    reportFormats: rt.reportFormats ? [...rt.reportFormats] : [],
    recipients: [''],
    emailHtmlBody: rt.template || '',
    locationIds: [],
    showUnpaid: false,
    saving: false
  }
}

export default {
  name: 'AutomatskiSlanje',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      config: { schedules: [], reportFormats: [], reportTypes: [] },
      rows: {},
      loading: false,
      openPanels: [],
      showHelp: true,
      confirmDialog: false,
      pendingDeactivate: null,
      deactivating: false,
      locations: []
    }
  },

  computed: {
    companyId () {
      return this.$route.params.companyId
    },
    companyPlaceholders () {
      const c = state.getCurrentCompany()
      const result = {}
      if (c.name) result.company_name = c.name
      if (c.oib) result.oib = c.oib
      if (c.address) result.address = c.address
      if (c.city) result.city = c.city
      if (c.zip_code) result.zip_code = c.zip_code
      return result
    }
  },

  mounted () {
    this.margin = state.getMargin()
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    this.init()
    this.loadLocations()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    async init () {
      this.loading = true
      try {
        const [cfg, subs] = await Promise.all([
          getReportConfig(),
          getSubscriptions(this.companyId)
        ])
        this.config = cfg
        this.buildRows(Array.isArray(subs) ? subs : [])
        this.openPanels = []
      } catch (e) {
        this.showMsgBox({ text: 'Greška pri učitavanju podataka.', color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
      } finally {
        this.loading = false
      }
    },

    buildRows (subs) {
      const rows = {}
      for (const schedule of this.config.schedules) {
        rows[schedule] = {}
        for (const rt of this.config.reportTypes) {
          rows[schedule][rt.id] = emptyRow(rt)
        }
      }
      for (const sub of subs) {
        if (rows[sub.schedule] && rows[sub.schedule][sub.reportType]) {
          rows[sub.schedule][sub.reportType] = {
            active: sub.active !== false,
            subId: sub.id,
            reportFormats: sub.reportFormats ? [...sub.reportFormats] : [],
            recipients: sub.recipients && sub.recipients.length ? [...sub.recipients] : [''],
            emailHtmlBody: sub.emailHtmlBody || '',
            locationIds: Array.isArray(sub.locationIds) ? [...sub.locationIds] : [],
            showUnpaid: sub.params?.show_unpaid === true,
            saving: false
          }
        }
      }
      this.rows = rows
    },

    row (schedule, typeId) {
      return this.rows[schedule]?.[typeId] || emptyRow({})
    },

    onToggle (schedule, typeId, val) {
      const r = this.rows[schedule]?.[typeId]
      if (!r) return
      if (!val && r.subId) {
        this.pendingDeactivate = { schedule, typeId }
        this.confirmDialog = true
        return
      }
      this.$set(this.rows[schedule], typeId, { ...r, active: !!val })
    },

    cancelDeactivate () {
      this.confirmDialog = false
      this.pendingDeactivate = null
    },

    async confirmDeactivate () {
      if (!this.pendingDeactivate) return
      const { schedule, typeId } = this.pendingDeactivate
      const r = this.rows[schedule][typeId]
      this.deactivating = true
      try {
        await deleteSubscription(r.subId)
        const rt = this.config.reportTypes.find(t => t.id === typeId)
        this.$set(this.rows[schedule], typeId, emptyRow(rt || { id: typeId }))
        this.confirmDialog = false
        this.pendingDeactivate = null
      } catch (e) {
        const msg = e?.response?.data?.error || 'Greška pri brisanju.'
        this.showMsgBox({ text: msg, color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
      } finally {
        this.deactivating = false
      }
    },

    async saveRow (schedule, typeId) {
      const r = this.rows[schedule]?.[typeId]
      if (!r) return
      const recipients = r.recipients.map(x => x.trim()).filter(Boolean)
      if (r.reportFormats.length === 0 || recipients.length === 0) {
        this.showMsgBox({ text: 'Odaberite barem jedan format i unesite barem jednog primatelja.', color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
        return
      }
      const rt = this.config.reportTypes.find(t => t.id === typeId)
      if (rt?.requiresLocation && r.locationIds.length === 0) {
        this.showMsgBox({ text: 'Odaberite barem jedno prodajno mjesto za ovaj izvještaj.', color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
        return
      }
      const payload = {
        companyId: this.companyId,
        reportType: typeId,
        schedule,
        reportFormats: r.reportFormats,
        recipients,
        active: true,
        emailHtmlBody: r.emailHtmlBody || '',
        placeholders: { ...this.companyPlaceholders },
        locationIds: rt?.requiresLocation ? r.locationIds : undefined,
        params: rt?.requiresLocation ? { show_unpaid: r.showUnpaid === true } : undefined
      }
      this.$set(this.rows[schedule][typeId], 'saving', true)
      try {
        if (r.subId) {
          await updateSubscription(r.subId, payload)
        } else {
          const created = await createSubscription(payload)
          this.$set(this.rows[schedule][typeId], 'subId', created.id)
        }
        this.$set(this.rows[schedule][typeId], 'recipients', recipients)
      } catch (e) {
        const msg = e?.response?.data?.error || 'Greška pri spremanju.'
        this.showMsgBox({ text: msg, color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
      } finally {
        this.$set(this.rows[schedule][typeId], 'saving', false)
      }
    },

    toggleFormat (schedule, typeId, fmt) {
      const r = this.rows[schedule]?.[typeId]
      if (!r) return
      const idx = r.reportFormats.indexOf(fmt)
      const formats = [...r.reportFormats]
      if (idx === -1) {
        formats.push(fmt)
      } else {
        formats.splice(idx, 1)
      }
      this.$set(this.rows[schedule][typeId], 'reportFormats', formats)
    },

    setRecipients (schedule, typeId, val) {
      if (!this.rows[schedule]?.[typeId]) return
      this.$set(this.rows[schedule][typeId], 'recipients', Array.isArray(val) ? val.filter(Boolean) : [])
    },

    openLog (schedule, typeId) {
      const subId = this.rows[schedule]?.[typeId]?.subId
      if (!subId) return
      this.$router.push({
        name: 'company.all-reports.automatski-slanje.log',
        params: { companyId: this.companyId },
        query: { ids: subId, type: this.reportTypeName(typeId) }
      })
    },

    reportTypesForSchedule (schedule) {
      return this.config.reportTypes.filter(rt =>
        !rt.allowedSchedules || rt.allowedSchedules.length === 0 || rt.allowedSchedules.includes(schedule)
      )
    },

    activeCount (schedule) {
      const scheduleRows = this.rows[schedule]
      if (!scheduleRows) return 0
      const allowed = new Set(this.reportTypesForSchedule(schedule).map(rt => rt.id))
      return Object.entries(scheduleRows).filter(([id, r]) => allowed.has(id) && r.active && r.subId).length
    },

    scheduleLabel (schedule) {
      return SCHEDULE_LABELS[schedule] || schedule
    },

    scheduleIcon (schedule) {
      return SCHEDULE_ICONS[schedule] || 'mdi-calendar'
    },

    reportTypeName (id) {
      const rt = this.config.reportTypes.find(t => t.id === id)
      return rt ? (rt.name || rt.id) : id
    },

    formatLabel (fmt) {
      return { pdf: 'PDF', xlsx: 'Excel' }[fmt] || fmt.toUpperCase()
    },

    formatIcon (fmt) {
      return { pdf: 'mdi-file-pdf-box', xlsx: 'mdi-file-excel' }[fmt] || 'mdi-file'
    },

    loadLocations () {
      const uid = auth.currentUser?.uid
      const companyId = this.companyId
      if (!uid || !companyId) return
      const docRef = df.doc(`user_locations/${uid}.${companyId}`)
      docRef.onSnapshot(doc => {
        if (!doc.exists || !doc.data()?.locations) return
        const locs = Object.values(doc.data().locations)
        locs.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        this.locations = locs
      })
    }
  }
}
</script>

<style scoped>
.page-title {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: 0.3px;
}

.info-banner {
  position: relative;
  background: #F0F9FF;
  border-left: 4px solid #0891B2;
  border-radius: 10px;
  padding: 18px 20px 16px;
}

.info-banner__close {
  position: absolute !important;
  top: -12px;
  right: -12px;
  color: #94A3B8 !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08) !important;
}

.info-banner__close:hover {
  color: #475569 !important;
}

.info-banner__schedule-times {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;
  padding: 10px 14px;
  background: rgba(8, 145, 178, 0.06);
  border-radius: 8px;
  border: 1px solid rgba(8, 145, 178, 0.15);
}

.info-banner__schedule-times-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #0891B2;
  width: 100%;
}

.info-banner__schedule-time-item {
  font-size: 12.5px;
  color: #334155;
}

.info-banner__intro {
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
  margin: 0 0 14px;
  padding-right: 24px;
}

.info-banner__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
}

.info-legend-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: #64748B;
  line-height: 1.5;
}

.info-legend-item strong {
  color: #1E293B;
}

.info-legend-item .v-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

@media (max-width: 600px) {
  .info-banner__grid {
    grid-template-columns: 1fr;
  }
}

/* ── Panels ── */
.schedule-panel {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
  overflow: hidden;
}

.panel-header {
  padding: 18px 20px !important;
  min-height: 60px !important;
}

.schedule-title {
  font-size: 17px;
  font-weight: 700;
  color: #1E293B;
  letter-spacing: -0.2px;
}

.active-chip {
  background: rgba(0, 188, 212, 0.1) !important;
  color: #00838F !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  height: 20px !important;
}

.inactive-chip {
  background: #F1F5F9 !important;
  color: #94A3B8 !important;
  font-weight: 500 !important;
  font-size: 11px !important;
  height: 20px !important;
}

.panel-content >>> .v-expansion-panel-content__wrap {
  padding: 0 !important;
  background: #EEF2F7;
}

/* ── Reports list & sub-cards ── */
.reports-list {
  padding: 16px;
}

.sub-card {
  border: 1px solid #DDE3EC;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: background 0.2s, border-color 0.2s;
  margin-bottom: 12px;
}

.sub-card:last-child {
  margin-bottom: 0;
}

.sub-card--inactive {
  background: #F8FAFC;
  border-color: #E8ECF2;
}

.sub-card--inactive .sub-card-title {
  color: #94A3B8;
}

.sub-card--inactive .row-switch {
  opacity: 0.7;
}

.sub-card-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  gap: 8px;
}

.sub-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
  letter-spacing: -0.2px;
}

.row-switch {
  flex-shrink: 0;
  margin-top: 0 !important;
}

.sub-card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  padding: 14px 16px;
  gap: 0;
}

.sub-card-body > .field-group:first-child {
  border-right: 1px solid #E2E8F0;
  padding-right: 20px;
}

.sub-card-body > .field-group:nth-child(2) {
  padding-left: 20px;
}

.sub-card-body--options {
  border-top: 1px solid #E2E8F0;
  background: #FAFBFD;
}

.sub-card-body--options > .field-group--switch {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.sub-card-footer {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #F8FAFC;
  border-top: 1px solid #E2E8F0;
  min-height: 46px;
  max-height: 52px;
  gap: 10px;
}

.recipient-chip {
  background: #EEF2F7 !important;
  color: #374151 !important;
  font-size: 12px !important;
}

.recipients-combobox {
  width: 100%;
}

.recipients-combobox >>> .v-chip {
  background: #EEF2F7 !important;
  color: #374151 !important;
  font-size: 12px !important;
}

.recipients-combobox >>> .v-chip .v-chip__content {
  gap: 4px;
}

@media (max-width: 600px) {
  .sub-card-body {
    grid-template-columns: 1fr;
  }

  .sub-card-body > .field-group:first-child {
    border-right: none;
    border-bottom: 1px solid #E2E8F0;
    padding-right: 0;
    padding-bottom: 14px;
  }

  .sub-card-body > .field-group:nth-child(2) {
    padding-left: 0;
    padding-top: 14px;
  }
}

/* ── Fields ── */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #9CA3AF;
}

/* ── Format pills ── */
.format-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.format-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.15s;
  white-space: nowrap;
  user-select: none;
}

.format-pill:hover {
  border-color: #B0BEC5;
  color: #37474F;
  background: #F5F5F5;
}

.format-pill.active-pdf {
  border-color: rgba(229, 57, 53, 0.4);
  background: rgba(229, 57, 53, 0.07);
  color: #B71C1C;
  font-weight: 600;
}

.format-pill.active-pdf >>> .v-icon {
  color: #E53935 !important;
}

.format-pill.active-xlsx {
  border-color: rgba(67, 160, 71, 0.4);
  background: rgba(67, 160, 71, 0.07);
  color: #2E7D32;
  font-weight: 600;
}

.format-pill.active-xlsx >>> .v-icon {
  color: #43A047 !important;
}

/* ── Recipients ── */
.recipient-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.recipient-input {
  flex: 1;
  max-width: 360px;
}

/* ── Buttons ── */
.save-btn {
  height: 32px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
  border: 1px solid rgba(0, 188, 212, 0.3) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.save-btn:hover:not(:disabled) {
  background: rgba(0, 188, 212, 0.14) !important;
  border-color: rgba(0, 188, 212, 0.6) !important;
  box-shadow: 0 2px 6px rgba(0, 188, 212, 0.2) !important;
}

.log-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  color: #3989C9 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.log-btn:hover {
  background: rgba(57, 137, 201, 0.07) !important;
}

.remove-recipient-btn {
  color: #B0BEC5 !important;
  transition: color 0.15s !important;
}

.remove-recipient-btn:hover {
  color: #E53935 !important;
}

.confirm-delete-btn {
  background: rgba(229, 57, 53, 0.1) !important;
  color: #C62828 !important;
  border: 1px solid rgba(229, 57, 53, 0.3) !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  box-shadow: none !important;
}

/* ── Sticky header ── */
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Filter input styling ── */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}
</style>
